// Manager app sidebar, built from the @shadcndesign app-shell-4 block and the
// Players Figma frame (TK4by1BCNqrKwv61XCVeD9, node 235:11751).
import React, { useEffect } from "react";

import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarRail,
  SidebarSeparator,
  SidebarTrigger,
  useSidebar,
} from "@/shadcn/components/ui/sidebar";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/shadcn/components/ui/tooltip";
import ClubSwitcher from "./ClubSwitcher";
import SidebarNavMenu from "./SidebarNavMenu";
import SidebarUserMenu from "./SidebarUserMenu";

const AppSidebar = ({
  club,
  clubs,
  selectedClubId,
  season,
  onClubChange,
  mainItems,
  superAdminItems,
  footerItems,
  pathname,
  user,
  showAccountSettings,
  onOpenAccountSettings,
  onLogout,
  toggleLabel,
}) => {
  const { isMobile, setOpenMobile } = useSidebar();
  const closeMobile = () => setOpenMobile(false);

  // A club switch or impersonation change leaves the mobile menu closed.
  useEffect(() => {
    setOpenMobile(false);
  }, [club?._id, user?._id, setOpenMobile]);

  // The mobile top bar already shows notifications.
  const visibleFooterItems = footerItems.filter(
    (item) => !(isMobile && item.hideOnMobile)
  );

  return (
    // Closed on desktop, it shrinks to its icons (Cmd/Ctrl+B or the rail).
    // Keep fixed navigation below the space reserved by inline page banners
    // and the impersonation banner. The mobile sheet ignores this class.
    <Sidebar
      collapsible="icon"
      className="top-[calc(var(--page-top-offset,0px)+var(--impersonation-banner-height,0px))] h-[calc(100svh-var(--page-top-offset,0px)-var(--impersonation-banner-height,0px))]">
      <nav aria-label="navigator" className="flex min-h-0 flex-1 flex-col">
        {/* Desktop fold toggle sits next to the club, always in view (a
            long menu pushes the footer off screen). Folded, it stacks under
            the logo. Mobile uses the top bar menu button. */}
        <SidebarHeader className="flex-row items-center gap-1 group-data-[collapsible=icon]:flex-col">
          <div className="min-w-0 flex-1 group-data-[collapsible=icon]:w-full">
            <ClubSwitcher
              club={club}
              clubs={clubs}
              selectedClubId={selectedClubId}
              season={season}
              onClubChange={onClubChange}
            />
          </div>
          {!isMobile && toggleLabel ? (
            <Tooltip>
              <TooltipTrigger
                render={
                  <SidebarTrigger aria-label={toggleLabel} className="shrink-0" />
                }
              />
              <TooltipContent side="right">{toggleLabel}</TooltipContent>
            </Tooltip>
          ) : null}
        </SidebarHeader>
        {/* shadcn hides overflow when folded; keep scrolling so a short
            window can still reach the lower icons. */}
        <SidebarContent className="group-data-[collapsible=icon]:overflow-y-auto!">
          {mainItems.length > 0 && (
            <SidebarNavMenu
              items={mainItems}
              pathname={pathname}
              onNavigate={closeMobile}
            />
          )}
          {superAdminItems.length > 0 && (
            <>
              <SidebarSeparator />
              <SidebarNavMenu
                items={superAdminItems}
                pathname={pathname}
                onNavigate={closeMobile}
              />
              <SidebarSeparator />
            </>
          )}
        </SidebarContent>
        <SidebarFooter>
          {visibleFooterItems.length > 0 && (
            <SidebarNavMenu
              items={visibleFooterItems}
              pathname={pathname}
              onNavigate={closeMobile}
              className="p-0"
            />
          )}
          <SidebarUserMenu
            user={user}
            showSettings={showAccountSettings}
            onOpenSettings={() => {
              closeMobile();
              onOpenAccountSettings();
            }}
            onLogout={onLogout}
          />
        </SidebarFooter>
      </nav>
      <SidebarRail />
    </Sidebar>
  );
};

export default AppSidebar;
